import { ConfirmationModalContext } from 'Components/ChatLayoutHeader/contexts/ConfirmationModalContext';
import { MobXProviderContext, observer } from 'mobx-react';
import { useDeleteConversationModal } from 'Modules/conversation/deleteConversation/hooks/useDeleteConversation';
import { canShowLeaveGroup, canShowMuteUnmute } from 'Modules/conversation/guards/conversationActionChecks';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import { useTogglePinConversation } from 'Modules/conversation/store/pinned-conversations/index.requests';
import React, { useCallback, useEffect, useState } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { bugsnagClient } from 'Utils/logUtils';
import { Button } from '~/lib/ui/button';
import {
  SidebarMenu,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenuItem,
} from '~/lib/ui/sidebar';
import { useConversationArchive } from '~/modules/conversation/hooks/useConversationArchive';
import { EntityProfileAction } from './EntityProfileAction';
import { EntityProfileActionsProps } from './index.types';

export const EntityProfileActions: React.FC<EntityProfileActionsProps> =
  observer(({ conversation }) => {
    const {
      conversation: conversationStore,
      ui: {
        selectIfConvMuted,
        setConvMutedDetailsTab,
        setMoreActionOptions,
        setGroupModal,
        setOpeningGroupModalFrom,
      },
      participant: { removeParticipantsDelete },
      person: { loggedInPersonId, loadPersonByIdGetIfMissingGet },
    } = React.useContext(MobXProviderContext) as RootStoreProps;

    const { setShow: setShowConfirmationModal } = React.useContext(
      ConfirmationModalContext
    );

    const { getIsConversationPinned } = useConversationStore();

    const isConversationArchived = !!conversation?.archived;

    const { openArchiveConversation } = useConversationArchive({
      conversationId: conversation?.id,
      isArchived: isConversationArchived,
      doWhenOpen: () => {
        setIsArchiveActionLoading(true);
      },
      doWhenConfirm: () => {
        const eventName = isConversationArchived
          ? 'conversation_detail_unarchive'
          : 'conversation_detail_archive';
        pushToGTMDataLayer(eventName);
      },
      doWhenFinished: () => {
        setMoreActionOptions({ show: false, conversationId: '' });
        setIsArchiveActionLoading(false);
      },
    });

    const { mutate: togglePinConversation } = useTogglePinConversation();

    const { handleDeleteConversation } = useDeleteConversationModal({
      conversation,
      onError: (error) => {
        bugsnagClient.notify(error as Error, (event) => {
          event.severity = 'error';
          event.context = 'EntityProfileActions.handleDeleteConversation';
          event.addMetadata('custom', {
            function: 'handleDeleteConversation',
            action: 'delete_conversation_attempt',
            conversationId: conversation?.id,
            code: error,
          });
          event.request = error;
        });
      },
    });

    const [isActiveParticipant, setIsActiveParticipant] = useState(false);

    // Loading states
    const [isMuteActionLoading, setIsMuteActionLoading] = useState(false);
    const [isFavoriteActionLoading, setIsFavoriteActionLoading] =
      useState(false);
    const [isArchiveActionLoading, setIsArchiveActionLoading] = useState(false);

    const isChannel = conversation?.grouping === 'Channel';

    const isConversationPinned = getIsConversationPinned(conversation?.id);
    const isConversationMuted = selectIfConvMuted(conversation?.id ?? '');

    // Initialize both muted and favorite status
    useEffect(() => {
      const initializeStates = () => {
        try {
          // Initialize active participant status
          if (conversation?.id) {
            setIsActiveParticipant(conversation?.isActiveParticipant);
          }
        } catch (error) {
          console.error('Failed to initialize states:', error);
        }
      };

      void initializeStates();
    }, [
      conversation?.id,
      conversation?.isActiveParticipant,
      selectIfConvMuted,
    ]);

    const updateMuteStatus = useCallback(
      (conversationId: string) => {
        const value = selectIfConvMuted(conversationId);
        setConvMutedDetailsTab(conversationId, !value);
        return !value;
      },
      [selectIfConvMuted, setConvMutedDetailsTab]
    );

    const toggleConversationMute = useCallback(async () => {
      if (!conversation?.id || isMuteActionLoading) return;

      if (isConversationMuted) {
        pushToGTMDataLayer('conversation_detail_unmuted');
      } else {
        pushToGTMDataLayer('conversation_detail_muted');
      }

      try {
        setIsMuteActionLoading(true);
        // add small delay to disalle action and avoid 304 errors
        await new Promise((resolve) => setTimeout(resolve, 100));
        updateMuteStatus(conversation?.id);
      } catch (error) {
        console.error('Failed to toggle conversation mute:', error);
      } finally {
        setIsMuteActionLoading(false);
      }
    }, [
      conversation?.id,
      isMuteActionLoading,
      isConversationMuted,
      updateMuteStatus,
    ]);

    const toggleConversationToFavorites = useCallback(async () => {
      if (!conversation?.id || isFavoriteActionLoading) return;

      if (isConversationPinned) {
        pushToGTMDataLayer('conversation_detail_unpin');
      } else {
        pushToGTMDataLayer('conversation_detail_pin');
      }

      try {
        setIsFavoriteActionLoading(true);

        // Add a small delay to ensure the loading state is visible
        const minDelay = new Promise((resolve) => setTimeout(resolve, 100));

        togglePinConversation(conversation?.id);

        await minDelay;
      } catch (error) {
        console.error('Failed to update favorites:', error);
      } finally {
        setIsFavoriteActionLoading(false);
      }
    }, [
      conversation?.id,
      isConversationPinned,
      isFavoriteActionLoading,
      togglePinConversation,
    ]);

    const showLeaveGroupModal = useCallback(() => {
      const onConfirm = async () => {
        pushToGTMDataLayer('group_details_leave');

        setIsActiveParticipant(false);

        void removeParticipantsDelete(conversation?.id, [
          {
            id: `${loggedInPersonId}_`,
            name: (
              await loadPersonByIdGetIfMissingGet(loggedInPersonId.toString())
            ).name,
          },
        ]).then(undefined, () => {
          setIsActiveParticipant(true);
        });
      };

      setShowConfirmationModal({ type: 'leaveGroup', onConfirm });
    }, [
      conversation?.id,
      loadPersonByIdGetIfMissingGet,
      loggedInPersonId,
      removeParticipantsDelete,
      setShowConfirmationModal,
    ]);

    const handleEditGroup = useCallback(() => {
      setGroupModal(true);
      conversationStore.setChannelInfoDetails(conversation?.id, 'edit');
      setMoreActionOptions({ show: false, conversationId: '' });
      setOpeningGroupModalFrom('edit');
    }, [
      conversation?.id,
      setGroupModal,
      conversationStore,
      setMoreActionOptions,
      setOpeningGroupModalFrom,
    ]);

    if (!conversation) {
      return null;
    }

    const showMuteUnmute = canShowMuteUnmute(conversation);

    const showLeaveGroup = canShowLeaveGroup(conversation);

    return (
      <SidebarGroup className="px-[7px]">
        <SidebarGroupLabel className="flex justify-between">
          Actions
          {isChannel && (
            <Button
              onClick={handleEditGroup}
              variant="link"
              size="sm"
              className="p-0 h-auto hover:no-underline"
            >
              Edit
            </Button>
          )}
        </SidebarGroupLabel>

        <SidebarMenu className="px-0">
          {showMuteUnmute && (
            <SidebarMenuItem className="px-0">
              <EntityProfileAction
                icon="muted"
                label="Mute notifications"
                onClick={() => void toggleConversationMute()}
                switchProps={{
                  checked: isConversationMuted,
                  onCheckedChange: () => void toggleConversationMute(),
                  disabled: isMuteActionLoading,
                }}
              />
            </SidebarMenuItem>
          )}

          <SidebarMenuItem className="px-0">
            <EntityProfileAction
              icon="pin"
              label="Pin conversation"
              onClick={() => void toggleConversationToFavorites()}
              switchProps={{
                checked: isConversationPinned,
                onCheckedChange: () => void toggleConversationToFavorites(),
                disabled: isFavoriteActionLoading || conversation.archived,
              }}
            />
          </SidebarMenuItem>

          <SidebarMenuItem className="px-0">
            <EntityProfileAction
              icon="archive"
              label={
                isConversationArchived
                  ? 'Unarchive conversation'
                  : 'Archive conversation'
              }
              onClick={openArchiveConversation}
              disabled={isArchiveActionLoading}
            />
          </SidebarMenuItem>

          {showLeaveGroup && (
            <SidebarMenuItem className="px-0">
              <EntityProfileAction
                icon="leave-group"
                label="Leave group"
                onClick={showLeaveGroupModal}
                disabled={!isActiveParticipant}
              />
            </SidebarMenuItem>
          )}

          <SidebarMenuItem className="px-0">
            <EntityProfileAction
              icon="delete"
              label="Delete conversation"
              onClick={handleDeleteConversation}
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>
    );
  });
